import { useState } from "react";
import { useParams, Link, useOutletContext } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, CornerDownRight } from "lucide-react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

export default function ThreadPage() {
  const { threadId } = useParams();
  const { user } = useAuth();
  const { openAuth } = useOutletContext();
  const qc = useQueryClient();
  const [reply, setReply] = useState("");

  const { data, isLoading } = useQuery({ queryKey: ["thread", threadId], queryFn: async () => (await api.get(`/forum/threads/${threadId}`)).data });

  const post = useMutation({
    mutationFn: async () => (await api.post(`/forum/threads/${threadId}/replies`, { body: reply })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["thread", threadId] }); setReply(""); toast.success("Reply posted"); },
    onError: (e) => toast.error(formatError(e.response?.data?.detail)),
  });

  if (isLoading || !data) return <div className="max-w-3xl mx-auto px-4 py-20 text-center text-muted-foreground">Loading…</div>;
  const { thread, replies } = data;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
      <Link to="/forum" className="inline-flex items-center gap-1 text-sm font-semibold text-muted-foreground hover:text-primary mb-6" data-testid="back-to-forum"><ArrowLeft className="w-4 h-4" /> Back to forum</Link>

      <article className="border border-border bg-white p-8" data-testid="thread-detail">
        <h1 className="font-display text-3xl font-bold tracking-tight">{thread.title}</h1>
        <p className="label-tech text-muted-foreground mt-2">Started by {thread.author_name}</p>
        <p className="mt-6 leading-relaxed whitespace-pre-wrap">{thread.body}</p>
      </article>

      <div className="mt-8">
        <h2 className="label-tech text-muted-foreground mb-4">{replies.length} Replies</h2>
        <div className="space-y-4">
          {replies.map((r) => (
            <div key={r.id} className="border border-border bg-white p-6 flex gap-4 animate-slide-up" data-testid={`reply-${r.id}`}>
              <div className="w-9 h-9 bg-primary text-white flex items-center justify-center font-bold shrink-0">{r.author_name?.[0]?.toUpperCase()}</div>
              <div>
                <p className="font-semibold text-sm">{r.author_name}</p>
                <p className="mt-1 leading-relaxed whitespace-pre-wrap">{r.body}</p>
              </div>
            </div>
          ))}
          {replies.length === 0 && <p className="text-sm text-muted-foreground">No replies yet.</p>}
        </div>
      </div>

      <div className="mt-8 border border-border bg-white p-6">
        <h3 className="font-display text-lg font-bold flex items-center gap-2 mb-3"><CornerDownRight className="w-4 h-4 text-primary" /> Add a reply</h3>
        {user ? (
          <>
            <Textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={4} className="rounded-none" placeholder="Share your knowledge…" data-testid="reply-input" />
            <Button onClick={() => post.mutate()} disabled={post.isPending || !reply.trim()} className="rounded-none bg-primary hover:bg-[#D62828] font-bold mt-3" data-testid="reply-submit-btn">
              {post.isPending ? "Posting…" : "Post Reply"}
            </Button>
          </>
        ) : (
          <Button onClick={openAuth} variant="outline" className="rounded-none border-foreground font-bold" data-testid="reply-signin-btn">Sign in to reply</Button>
        )}
      </div>
    </div>
  );
}
